import React, { useEffect, useState } from 'react';
import { History, Loader2, RefreshCw } from 'lucide-react';
import { supportAPI } from '../../services/supportApi';
import { dtfmt } from './supportCommon';

/**
 * Support → «Ημερολόγιο» (25/10/2026): ό,τι έκαναν support και super admin σε όλα τα καταστήματα (απόφαση χρήστη),
 * από το audit_logs. Φίλτρα ανά κατάστημα / άτομο / ενέργεια, 50 ανά σελίδα με «Περισσότερα».
 */

interface AuditEntry {
  id: number;
  created_at: string;
  action: string;
  entity_type: string;
  entity_id: number | null;
  store_id: number | null;
  store_name: string | null;
  employee_id: number;
  employee_name: string;
  role_name: string;
  old_values: Record<string, any> | null;
  new_values: Record<string, any> | null;
}
interface Person { id: number; name: string; role_name: string }

// [ενέργεια:οντότητα] → ετικέτα· ό,τι δεν είναι εδώ φαίνεται ως «ενέργεια · οντότητα».
const LABELS: Record<string, string> = {
  'create:store': 'Νέο κατάστημα',
  'reset_password:store': 'Νέος κωδικός καταστήματος',
  'reset_pin:employee': 'Νέο PIN υπαλλήλου',
  'retry:fiscal_document': 'Επανάληψη παραστατικού',
  'cancel:order': 'Ακύρωση παραγγελίας',
  'cancel:fiscal_document': 'Ακύρωση παραστατικού',
  'update:cashier_devices': 'Συσκευές ταμείου',
  'command:cashier_devices': 'Εντολή σε PC ταμείου',
  'create:billing_payment': 'Καταχώριση πληρωμής πάγιου',
  'delete:billing_payment': 'Διαγραφή πληρωμής πάγιου',
  'create:store_billing': 'Στοιχεία χρέωσης',
  'update:store_billing': 'Αλλαγή χρέωσης',
  'update:store_billing_features': 'Χρεώσιμες λειτουργίες',
  'create:store_hardware': 'Εξοπλισμός',
  'update:store_hardware': 'Αλλαγή εξοπλισμού',
  'delete:store_hardware': 'Διαγραφή εξοπλισμού',
  'join_chain:store': 'Κατάστημα σε αλυσίδα',
  'leave_chain:store': 'Κατάστημα εκτός αλυσίδας',
  'rename:store': 'Ονομασία υποκαταστήματος',
  'price_list:store': 'Αλλαγή τιμοκαταλόγου',
  'create:organization_member': 'Νέος χρήστης αλυσίδας',
  'add:organization_member': 'Χρήστης σε αλυσίδα',
  'update:organization_member': 'Ρόλος / πρόσβαση χρήστη αλυσίδας',
  'remove:organization_member': 'Χρήστης εκτός αλυσίδας',
  'reset_password:org_user': 'Νέος κωδικός χρήστη αλυσίδας',
  'connect:platform': 'Σύνδεση πλατφόρμας',
  'update:platform': 'Αλλαγή πλατφόρμας',
  'disconnect:platform': 'Αποσύνδεση πλατφόρμας',
  'agent_target:store': 'Στόχος έκδοσης agent',
  'agent_target_clear:store': 'Καθαρισμός στόχου agent',
};
const ACTIONS: Array<[string, string]> = [
  ['create', 'Δημιουργία'], ['reset_password', 'Νέος κωδικός'], ['reset_pin', 'Νέο PIN'], ['retry', 'Επανάληψη'],
  ['cancel', 'Ακύρωση'], ['update', 'Αλλαγή'], ['command', 'Εντολή'], ['delete', 'Διαγραφή'],
  ['join_chain', 'Σε αλυσίδα'], ['leave_chain', 'Εκτός αλυσίδας'], ['price_list', 'Τιμοκατάλογος'], ['rename', 'Ονομασία'],
  ['add', 'Προσθήκη χρήστη'], ['remove', 'Αφαίρεση χρήστη'], ['connect', 'Σύνδεση πλατφόρμας'], ['disconnect', 'Αποσύνδεση πλατφόρμας'],
  ['agent_target', 'Στόχος agent'], ['agent_target_clear', 'Καθαρισμός στόχου agent'],
];
const ROLE = (r: string) => (r === 'super_admin' ? 'super admin' : r === 'support' ? 'support' : r);
const BILLING_FIELD: Record<string, string> = {
  installed_at: 'εγκατάσταση', billing_cycle: 'κύκλος', agreed_price: 'τιμή', provider_doc_quota: 'πακέτο',
  contract_end: 'λήξη', tracking_from: 'καταγραφή από', notes: 'σημειώσεις',
};

const label = (e: AuditEntry) => LABELS[`${e.action}:${e.entity_type}`] || `${e.action} · ${e.entity_type}`;

/** Μία γραμμή «τι ακριβώς» από τα πριν/μετά — ποτέ ολόκληρο το JSON. */
const detail = (e: AuditEntry): string => {
  const nv = e.new_values || {};
  const ov = e.old_values || {};
  switch (`${e.action}:${e.entity_type}`) {
    case 'create:store':
      return [nv.name, nv.organization_id ? `αλυσίδα #${nv.organization_id}` : null, nv.manager_email ? `διαχειριστής ${nv.manager_email}` : null]
        .filter(Boolean).join(' · ');
    case 'retry:fiscal_document':
      return `#${e.entity_id}: ${ov.status || '—'} → ${nv.status || '—'}`;
    case 'cancel:order':
      return `#${e.entity_id}${nv.reason ? ` · ${String(nv.reason).replace(/^\[SUPPORT\]\s*/, '')}` : ''}`;
    case 'reset_pin:employee':
      return `υπάλληλος #${e.entity_id}`;
    case 'join_chain:store':
    case 'leave_chain:store':
      return nv.organization || ov.organization || (nv.organization_id ? `αλυσίδα #${nv.organization_id}` : '');
    case 'price_list:store':
      return `τιμοκατάλογος ${ov.price_list_id ?? 'βασικός'} → ${nv.price_list_id ?? 'βασικός'}`;
    case 'rename:store':
      return `«${ov.display_name || '—'}» → «${nv.display_name || '—'}»`;
    case 'create:organization_member':
    case 'add:organization_member':
    case 'update:organization_member':
    case 'remove:organization_member':
      return [nv.organization, nv.email, nv.role, ov.role && nv.role && ov.role !== nv.role ? `από ${ov.role}` : null].filter(Boolean).join(' · ');
    case 'connect:platform':
    case 'update:platform':
    case 'disconnect:platform':
      return [nv.platform || ov.platform, nv.restaurant_id || ov.restaurant_id ? `id ${nv.restaurant_id || ov.restaurant_id}` : null,
        Array.isArray(nv.changed) && nv.changed.length ? `άλλαξαν: ${nv.changed.join(', ')}` : null].filter(Boolean).join(' · ');
    case 'agent_target:store':
    case 'agent_target_clear:store':
      return `${ov.target_version || '—'} → ${nv.target_version || '—'}${nv.offered_now ? ` · προσφέρθηκε σε ${nv.offered_now} PC` : ''}`;
    case 'reset_password:org_user':
      return `χρήστης #${e.entity_id}`;
    case 'command:cashier_devices':
    case 'update:cashier_devices':
      return [nv.machine, nv.op].filter(Boolean).join(' · ');
    case 'create:store_billing':
    case 'update:store_billing': {
      // Μόνο ό,τι άλλαξε, «πριν → μετά»· οι σημειώσεις όχι αυτούσιες.
      const keys: string[] = Array.isArray(nv.changed) ? nv.changed : [];
      const show = (k: string, v: any) => (v == null || v === '' ? '—' : k === 'notes' ? '…' : String(v).slice(0, 10));
      return [nv.source === 'bulk' ? 'μαζικά' : null,
        ...keys.map(k => `${BILLING_FIELD[k] || k}: ${show(k, ov[k])} → ${show(k, nv[k])}`)].filter(Boolean).join(' · ');
    }
    case 'update:store_billing_features':
      return `${(ov.features || []).join(', ') || '—'} → ${(nv.features || []).join(', ') || '—'}`;
    case 'create:store_hardware':
    case 'update:store_hardware':
    case 'delete:store_hardware': {
      const h = e.action === 'delete' ? ov : nv;
      return [h.item, h.price != null ? `${h.price} €` : null, h.paid_amount != null ? `εξοφλ. ${h.paid_amount} €` : null].filter(Boolean).join(' · ');
    }
    default: {
      const keys = Object.keys(nv).filter(k => !['source', 'by'].includes(k)).slice(0, 3);
      return keys.map(k => `${k}: ${typeof nv[k] === 'object' ? '…' : String(nv[k])}`).join(' · ');
    }
  }
};

const SELECT = 'bg-surface-2 dark:bg-surface-1 border border-line text-t1 rounded-lg px-3 py-2 text-sm';

const SupportAuditTab: React.FC = () => {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [stores, setStores] = useState<Array<{ id: number; name: string; display_name?: string | null }>>([]);
  const [next, setNext] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const [storeId, setStoreId] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [action, setAction] = useState('');

  const params = (before?: number) => ({
    ...(storeId ? { store_id: Number(storeId) } : {}),
    ...(employeeId ? { employee_id: Number(employeeId) } : {}),
    ...(action ? { action } : {}),
    ...(before ? { before } : {}),
  });

  const load = async () => {
    setLoading(true); setFailed(false);
    try {
      const d: any = await supportAPI.getAudit(params());
      setEntries(d?.entries || []); setNext(d?.next_before ?? null); setPeople(d?.people || []);
    } catch { setFailed(true); setEntries([]); setNext(null); } finally { setLoading(false); }
  };
  const loadMore = async () => {
    if (!next) return;
    setMore(true);
    try {
      const d: any = await supportAPI.getAudit(params(next));
      // Χωρίς διπλές γραμμές αν η σελίδα «συναντήσει» την προηγούμενη (π.χ. αλλαγή φίλτρου όσο φόρτωνε).
      setEntries(prev => {
        const seen = new Set(prev.map(e => e.id));
        return [...prev, ...((d?.entries || []) as AuditEntry[]).filter(e => !seen.has(e.id))];
      });
      setNext(d?.next_before ?? null);
    } catch { setFailed(true); } finally { setMore(false); }
  };

  useEffect(() => {
    (async () => { try { const d: any = await supportAPI.getStores(); setStores(d?.stores || []); } catch { setStores([]); } })();
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [storeId, employeeId, action]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-5" data-testid="support-audit">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-t1 flex items-center gap-2">
            <History className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />Ημερολόγιο
          </h1>
          <p className="text-sm text-t3">Τι έκαναν support και super admin, σε όλα τα καταστήματα</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Κατάστημα" className={SELECT} value={storeId} onChange={(e) => setStoreId(e.target.value)}>
            <option value="">Όλα τα καταστήματα</option>
            {stores.map(s => <option key={s.id} value={String(s.id)}>#{s.id} · {s.display_name || s.name}</option>)}
          </select>
          <select aria-label="Άτομο" className={SELECT} value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="">Όλοι</option>
            {people.map(p => <option key={p.id} value={String(p.id)}>{p.name || `#${p.id}`} ({ROLE(p.role_name)})</option>)}
          </select>
          <select aria-label="Ενέργεια" className={SELECT} value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">Όλες οι ενέργειες</option>
            {ACTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button onClick={load} className="p-2 text-t3 hover:text-t1 bg-surface-2 dark:bg-surface-1 border border-line rounded-lg" title="Ανανέωση">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><RefreshCw className="h-6 w-6 animate-spin text-cyan-600 dark:text-cyan-400" /></div>
      ) : failed && entries.length === 0 ? (
        <p className="text-sm text-red-700 dark:text-red-300 text-center py-12">Το ημερολόγιο δεν φορτώθηκε.</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-t4 text-center py-12">Καμία ενέργεια με αυτά τα φίλτρα.</p>
      ) : (
        <ul className="divide-y divide-line border border-line-strong rounded-xl overflow-hidden">
          {entries.map(e => (
            <li key={e.id} className="px-4 py-3 bg-surface-2 dark:bg-surface-1 flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4" data-testid={`support-audit-${e.id}`}>
              <span className="text-xs text-t3 sm:w-36 shrink-0 tabular-nums">{dtfmt(e.created_at)}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-t1">
                  <b>{label(e)}</b>
                  {e.store_id != null && <span className="text-t3"> · #{e.store_id} {e.store_name || ''}</span>}
                </p>
                {detail(e) && <p className="text-xs text-t3 break-words">{detail(e)}</p>}
              </div>
              <span className="text-xs text-t2 shrink-0">
                {e.employee_name || `#${e.employee_id}`}
                <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-500/10 text-cyan-700 dark:text-cyan-300">{ROLE(e.role_name)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {next && !loading && (
        <div className="flex justify-center">
          <button type="button" onClick={loadMore} disabled={more}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-t2 bg-surface-2 dark:bg-surface-1 border border-line rounded-lg hover:text-t1 disabled:opacity-50">
            {more && <Loader2 className="h-4 w-4 animate-spin" />}Περισσότερα
          </button>
        </div>
      )}
    </div>
  );
};

export default SupportAuditTab;
